/* eslint-disable no-console */
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { convertMarkdownToProsemirror } from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';

import { adfResponseToFragment } from '../../../ui/utils/request/adf-response';
import type { ADFResponse } from '../../ai-session-context/types';

/**
 * Converts ADF response to ProseMirror Fragment
 */
export function convertADFToFragment(schema: Schema, adfResponse?: ADFResponse): Fragment | null {
	if (!adfResponse) {
		return null;
	}

	try {
		const pmNode = adfResponseToFragment({ schema, adfResponse });
		return pmNode;
	} catch {
		return null;
	}
}

interface ConvertMarkdownOptions {
	/** When true, enables mentions regardless of feature gate. Defaults to feature-gated. */
	forceMentions?: boolean;
}

/**
 * Converts Markdown Plus text to a ProseMirror Fragment using the idMap for
 * rich-node resolution (mentions, etc.).
 *
 * By default, mentions are gated behind the `platform_editor_ai_draft_reply_mentions`
 * feature flag. Pass `{ forceMentions: true }` to always enable mentions
 * (used by the free-gen fallback path).
 *
 * Falls back to a plain-text paragraph on conversion failure.
 */
export function convertMarkdownToFragment(
	schema: Schema,
	markdown: string | undefined,
	idMap: IDMap | undefined,
	options?: ConvertMarkdownOptions,
): Fragment | null {
	if (!markdown) {
		return null;
	}
	try {
		const { pmFragment } = convertMarkdownToProsemirror({
			schema,
			markdown,
			idMap: (idMap as Record<string, never>) ?? {},
			featureToggles: {
				markdownPlus: true,
				markdownPlusExtensions: true,
				markdownPlusPanels: true,
				markdownPlusDecisions: true,
				markdownPlusMentions:
					options?.forceMentions || fg('platform_editor_ai_draft_reply_mentions'),
				markdownPlusRemoveUnclosedTokens: true,
				allowAttributesMatchDefault: true,
			},
		});
		return pmFragment;
	} catch {
		return Fragment.from(schema.nodes.paragraph.create(null, schema.text(markdown)));
	}
}

interface ResolveContentFragmentOptions {
	/** When true, skip conversion if markdown ends with an incomplete custom tag.
	 *  Used during streaming to avoid inserting malformed mention/custom nodes. */
	skipIncompleteCustomTags?: boolean;
}

/**
 * Resolves AI response content to a ProseMirror Fragment using a prioritised
 * conversion chain:
 *
 * 1. ADF response → Fragment (preferred when adfPrompt is enabled)
 * 2. Markdown → Fragment via experiment-gated `convertMarkdownToFragment`
 *    (respects the `show_mentions_in_suggest_reply` experiment)
 * 3. Markdown → Fragment with mentions always enabled (free-gen fallback)
 * 4. Plain-text paragraph (last resort on conversion failure)
 *
 * Returns `null` only when no content is available at all or
 * `skipIncompleteCustomTags` suppresses a partial custom tag.
 */
export function resolveContentFragment(
	schema: Schema,
	adfResponse: ADFResponse | undefined,
	markdown: string | undefined,
	idMap: IDMap | undefined,
	options?: ResolveContentFragmentOptions,
): Fragment | null {
	// 1. Try ADF first
	const adfFragment = convertADFToFragment(schema, adfResponse);
	if (adfFragment) {
		return adfFragment;
	}

	// 2. Try experiment-gated markdown conversion (uses feature-gated mentions)
	if (isExperimentEnabled('show_mentions_in_suggest_reply')) {
		const mdFragment = convertMarkdownToFragment(schema, markdown, idMap);
		if (mdFragment) {
			return mdFragment;
		}
	}

	// 3. Fallback: direct markdown conversion with mentions always enabled
	if (markdown) {
		if (options?.skipIncompleteCustomTags) {
			// Skip conversion if the markdown ends with an incomplete custom tag (e.g. <cust, <custom)
			// to avoid inserting malformed mention/custom nodes during streaming.
			const incompleteCustomTagPattern = new RegExp(
				'<(c(u(s(t(o(m(?!.*<\\/custom>).*?)?)?)?)?)?)?$',
				'gu',
			);
			const hasIncompleteCustomTag = !!markdown.match(incompleteCustomTagPattern);
			if (hasIncompleteCustomTag) {
				return null;
			}
		}

		return convertMarkdownToFragment(schema, markdown, idMap, { forceMentions: true });
	}

	return null;
}

/**
 * Validates fragment health by checking each node
 */
export function fragmentIsHealthy(fragment: Fragment): boolean {
	let isHealthy = true;

	fragment.forEach((node) => {
		if (!isHealthy) {
			return;
		}
		try {
			node.check();
		} catch {
			isHealthy = false;
		}
	});

	return isHealthy;
}

/**
 * Calculates the telepointer position by walking the nodes between startPos
 * and endPos, finding the end of the last leaf node.
 *
 * Returns endPos as fallback if no leaf nodes are found or positions are invalid.
 */
export function calculateTelepointerPos(
	doc: {
		nodesBetween: (
			from: number,
			to: number,
			fn: (node: { isLeaf: boolean; nodeSize: number }, pos: number) => void,
		) => void;
	},
	startPos: number,
	endPos: number,
): number {
	let telepointerPos = endPos;
	try {
		doc.nodesBetween(startPos, endPos, (node, pos) => {
			if (node.isLeaf) {
				telepointerPos = pos + node.nodeSize;
			}
		});
		telepointerPos = Math.min(telepointerPos, endPos);
	} catch {
		telepointerPos = endPos;
	}
	return telepointerPos;
}
